/**
 * Admin Toolbar Context
 *
 * Provides scenario selection capability across the app for the AdminToolbar.
 * This allows AnalyzerPage to provide scenarios to the toolbar.
 */

import React, { createContext, useContext, useState, useCallback } from 'react'
import type { Scenario, ResponseDraftScenario } from '@admin/components/MockScenarios'

interface AdminToolbarContextType {
  // Scenarios
  demoScenarios: Scenario[]
  assessorScenarios: Scenario[]
  responseDraftScenarios: ResponseDraftScenario[]
  setDemoScenarios: (scenarios: Scenario[]) => void
  setAssessorScenarios: (scenarios: Scenario[]) => void
  setResponseDraftScenarios: (scenarios: ResponseDraftScenario[]) => void

  // Scenario selection callbacks
  onScenarioSelect: ((scenario: Scenario) => void) | undefined
  onResponseDraftSelect: ((scenario: ResponseDraftScenario) => void) | undefined
  setOnScenarioSelect: (callback: ((scenario: Scenario) => void) | undefined) => void
  setOnResponseDraftSelect: (
    callback: ((scenario: ResponseDraftScenario) => void) | undefined,
  ) => void

  // Current step (for scenario filtering)
  currentStep: number | string | undefined
  setCurrentStep: (step: number | string | undefined) => void

  // V3 Response toggle (admin testing)
  useV3Response: boolean
  setUseV3Response: (useV3: boolean) => void

  // Tour visibility (admin testing)
  showTour: boolean
  setShowTour: (show: boolean) => void

  // Support chat visibility (admin testing)
  showSupportChat: boolean
  setShowSupportChat: (show: boolean) => void

  // Restart step 3 callback (admin testing)
  onRestartStep3: (() => void) | undefined
  setOnRestartStep3: (callback: (() => void) | undefined) => void

  /** Lens results: open extracted-content debug modal */
  onShowLensContentDebug: (() => void) | undefined
  setOnShowLensContentDebug: (callback: (() => void) | undefined) => void

  /** Lens results: pipeline scoring debug side panel */
  showLensPipelineDebug: boolean
  setShowLensPipelineDebug: (show: boolean) => void

  /** Right-side response draft panel (analysis results page) */
  responseSidePanelOpen: boolean
  setResponseSidePanelOpen: (open: boolean) => void
}

const AdminToolbarContext = createContext<AdminToolbarContextType | null>(null)

export const useAdminToolbar = () => {
  const context = useContext(AdminToolbarContext)
  if (!context) {
    throw new Error('useAdminToolbar must be used within AdminToolbarProvider')
  }
  return context
}

interface AdminToolbarProviderProps {
  children: React.ReactNode
}

export const AdminToolbarProvider: React.FC<AdminToolbarProviderProps> = ({ children }) => {
  const [demoScenarios, setDemoScenarios] = useState<Scenario[]>([])
  const [assessorScenarios, setAssessorScenarios] = useState<Scenario[]>([])
  const [responseDraftScenarios, setResponseDraftScenarios] = useState<ResponseDraftScenario[]>([])
  const [onScenarioSelect, setOnScenarioSelect] = useState<
    ((scenario: Scenario) => void) | undefined
  >()
  const [onResponseDraftSelect, setOnResponseDraftSelect] = useState<
    ((scenario: ResponseDraftScenario) => void) | undefined
  >()
  const [onRestartStep3, setOnRestartStep3] = useState<(() => void) | undefined>()
  const [onShowLensContentDebug, setOnShowLensContentDebug] = useState<(() => void) | undefined>()
  const [currentStep, setCurrentStep] = useState<number | string | undefined>()
  const [useV3Response, setUseV3Response] = useState<boolean>(false) // Default to v2
  const [showTour, setShowTour] = useState<boolean>(false)
  const [showLensPipelineDebug, setShowLensPipelineDebugState] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('admin-lens-pipeline-debug') === 'true'
    }
    return false
  })
  const [responseSidePanelOpen, setResponseSidePanelOpen] = useState(false)
  const [showSupportChat, setShowSupportChatState] = useState<boolean>(() => {
    // Load from sessionStorage on mount
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('admin-show-support-chat')
      return saved === 'true'
    }
    return false
  })

  // Wrapper to persist to sessionStorage
  const setShowSupportChat = useCallback((show: boolean) => {
    setShowSupportChatState(show)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('admin-show-support-chat', show.toString())
    }
  }, [])

  const setShowLensPipelineDebug = useCallback((show: boolean) => {
    setShowLensPipelineDebugState(show)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('admin-lens-pipeline-debug', show.toString())
    }
  }, [])

  const value: AdminToolbarContextType = {
    demoScenarios,
    assessorScenarios,
    responseDraftScenarios,
    setDemoScenarios,
    setAssessorScenarios,
    setResponseDraftScenarios,
    onScenarioSelect,
    onResponseDraftSelect,
    setOnScenarioSelect: useCallback((callback) => {
      setOnScenarioSelect(() => callback)
    }, []),
    setOnResponseDraftSelect: useCallback((callback) => {
      setOnResponseDraftSelect(() => callback)
    }, []),
    currentStep,
    setCurrentStep,
    useV3Response,
    setUseV3Response,
    showTour,
    setShowTour,
    showSupportChat,
    setShowSupportChat,
    onRestartStep3,
    setOnRestartStep3: useCallback((callback) => {
      setOnRestartStep3(() => callback)
    }, []),
    onShowLensContentDebug,
    setOnShowLensContentDebug: useCallback((callback) => {
      setOnShowLensContentDebug(() => callback)
    }, []),
    showLensPipelineDebug,
    setShowLensPipelineDebug,
    responseSidePanelOpen,
    setResponseSidePanelOpen,
  }

  return <AdminToolbarContext.Provider value={value}>{children}</AdminToolbarContext.Provider>
}
